import { useEffect, useMemo, useState } from "react";
import { EyeOff, LayoutGrid, Rows3, Star } from "lucide-react";
import { useSearchParams } from "react-router-dom";

import {
  addVenueToBooking,
  dismissGraphVenue,
  restoreGraphVenue,
  saveGraphVenue,
  unsaveGraphVenue,
  type GraphVenue,
} from "@/api/discover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PitchSheet from "./PitchSheet";
import VenueDetailSheet from "./VenueDetailSheet";
import { formatContactedRecency, formatLocation, friendlyError } from "./discoverCopy";

type Props = {
  artistId: string;
  venues: GraphVenue[];
  comparableCount: number;
  loading: boolean;
  error: string | null;
  artistFilter: { id: number; name: string } | null;
  onClearArtistFilter: () => void;
  /** Server-confirmed private state after Save / Unsave / Hide / Restore / Add to Booking. */
  onVenuePrivateState: (globalVenueId: string, privateState: GraphVenue["private_state"]) => void;
  onVenuePitchState: (globalVenueId: string, state: "prospect" | "contacted") => void;
  onGoToArtists: () => void;
};

/**
 * Booking position for a venue, from existing artist_venues state only. Saved is a
 * separate Discover bookmark (private_state.saved) and never a pipeline position.
 */
export type BookingState = "none" | "prospect" | "contacted";

export function bookingState(venue: GraphVenue): BookingState {
  const status = (venue.private_state.status ?? "").toLowerCase();
  if (status === "contacted") return "contacted";
  if (status === "prospect") return "prospect";
  return "none";
}

const BOOKING_FILTER_LABELS: Record<BookingState, string> = {
  none: "Not in Booking",
  prospect: "Prospecting",
  contacted: "Contacted",
};

/** Discover lists: comparable-derived recommendations, the user's bookmarks, and hidden venues. */
type ListId = "recommended" | "saved" | "hidden";

function inList(venue: GraphVenue, list: ListId): boolean {
  if (list === "saved") return venue.private_state.saved;
  if (list === "hidden") return Boolean(venue.private_state.dismissed);
  return !venue.private_state.dismissed && venue.in_comparable_graph !== false;
}

/** Region filter key: state/region when known, otherwise country. */
const regionOf = (venue: GraphVenue): string | null => venue.region?.trim() || venue.country?.trim() || null;

const selectClass = "mt-1 rounded-md border border-border bg-background px-2 py-2 text-sm text-foreground";

/** `?view=table` / `?view=cards`; Cards is the default. A future Map mode belongs here too. */
type ViewMode = "cards" | "table";
const parseView = (value: string | null): ViewMode => (value === "table" ? "table" : "cards");

export default function DiscoverVenuesPanel({
  artistId,
  venues,
  comparableCount,
  loading,
  error,
  artistFilter,
  onClearArtistFilter,
  onVenuePrivateState,
  onVenuePitchState,
  onGoToArtists,
}: Props) {
  const [query, setQuery] = useState("");
  const [region, setRegion] = useState("all");
  const [comparableId, setComparableId] = useState(artistFilter ? String(artistFilter.id) : "all");
  const [status, setStatus] = useState<"all" | BookingState>("all");
  const [minArtists, setMinArtists] = useState(1);
  const [list, setList] = useState<ListId>("recommended");
  const [searchParams, setSearchParams] = useSearchParams();
  const view = parseView(searchParams.get("view"));
  const changeView = (next: ViewMode) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("view", next);
        return params;
      },
      { replace: true },
    );
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});
  // Track the open venue by key so the detail reflects state changes made from it.
  const [openVenueKey, setOpenVenueKey] = useState<string | null>(null);
  const [pitchVenue, setPitchVenue] = useState<GraphVenue | null>(null);

  // "View venues" on a comparable artist arrives as a filter from the Artists tab.
  useEffect(() => {
    setComparableId(artistFilter ? String(artistFilter.id) : "all");
  }, [artistFilter]);

  const regionOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const venue of venues) {
      const key = regionOf(venue);
      if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [venues]);

  const comparableOptions = useMemo(() => {
    const names = new Map<number, string>();
    for (const venue of venues) for (const artist of venue.comparable_artists) names.set(artist.public_artist_id, artist.name);
    if (artistFilter) names.set(artistFilter.id, artistFilter.name);
    return [...names.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [venues, artistFilter]);

  const maxObserved = useMemo(() => Math.max(1, ...venues.map((v) => v.comparable_artist_count)), [venues]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return venues.filter((venue) => {
      if (!inList(venue, list)) return false;
      if (comparableId !== "all" && !venue.comparable_artists.some((a) => String(a.public_artist_id) === comparableId)) return false;
      if (region !== "all" && regionOf(venue) !== region) return false;
      if (status !== "all" && bookingState(venue) !== status) return false;
      // Saved-earlier venues have no current comparable evidence; the "Played by" floor doesn't apply.
      if (venue.in_comparable_graph !== false && venue.comparable_artist_count < minArtists) return false;
      if (term) {
        const haystack = [venue.name, venue.city, venue.region, venue.country].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [venues, query, region, comparableId, status, minArtists, list]);

  const listCounts = useMemo(
    () => ({
      recommended: venues.filter((venue) => inList(venue, "recommended")).length,
      saved: venues.filter((venue) => inList(venue, "saved")).length,
      hidden: venues.filter((venue) => inList(venue, "hidden")).length,
    }),
    [venues],
  );
  const filtersActive =
    Boolean(query.trim()) || region !== "all" || comparableId !== "all" || status !== "all" || minArtists > 1;
  const clearFilters = () => {
    setQuery("");
    setRegion("all");
    setStatus("all");
    setMinArtists(1);
    setComparableId("all");
    if (artistFilter) onClearArtistFilter();
  };
  const handleComparableChange = (value: string) => {
    setComparableId(value);
    if (artistFilter && value !== String(artistFilter.id)) onClearArtistFilter();
  };

  const handleDismissal = async (venue: GraphVenue, dismiss: boolean) => {
    if (!venue.global_venue_id) return;
    const id = venue.global_venue_id;
    setDismissingId(id);
    clearError(id);
    try {
      const response = dismiss ? await dismissGraphVenue(id, artistId) : await restoreGraphVenue(id, artistId);
      onVenuePrivateState(id, response.private_state);
    } catch (err) {
      const fallback = dismiss ? "Couldn't hide this venue. Please try again." : "Couldn't restore this venue. Please try again.";
      setSaveErrors((prev) => ({ ...prev, [id]: friendlyError(err, fallback) }));
    } finally {
      setDismissingId(null);
    }
  };

  const clearError = (id: string) =>
    setSaveErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  /** Star: bookmark / un-bookmark only. Never creates or changes a Booking record. */
  const handleSaveToggle = async (venue: GraphVenue) => {
    if (!venue.global_venue_id) return;
    const id = venue.global_venue_id;
    const unsave = venue.private_state.saved;
    setSavingId(id);
    clearError(id);
    try {
      if (unsave) {
        const response = await unsaveGraphVenue(id, artistId);
        onVenuePrivateState(id, response.private_state);
      } else {
        await saveGraphVenue(id, artistId);
        // Save also clears a private Hide; Booking state is untouched.
        onVenuePrivateState(id, {
          ...venue.private_state,
          has_relationship: true,
          saved: true,
          status: venue.private_state.status ?? "saved",
          dismissed: false,
          dismissed_at: null,
        });
      }
    } catch (err) {
      const fallback = unsave ? "Couldn't remove this venue from Saved. Please try again." : "Couldn't save this venue. Please try again.";
      setSaveErrors((prev) => ({ ...prev, [id]: friendlyError(err, fallback) }));
    } finally {
      setSavingId(null);
    }
  };

  /** Add to Booking: create/reuse the Booking record at Prospecting. Does not imply contact. */
  const handleAddToBooking = async (venue: GraphVenue) => {
    if (!venue.global_venue_id) return;
    const id = venue.global_venue_id;
    setBookingId(id);
    clearError(id);
    try {
      const response = await addVenueToBooking(id, artistId);
      onVenuePrivateState(id, response.private_state);
    } catch (err) {
      setSaveErrors((prev) => ({ ...prev, [id]: friendlyError(err, "Couldn't add this venue to Booking. Please try again.") }));
    } finally {
      setBookingId(null);
    }
  };

  if (loading && venues.length === 0) {
    return <p className="text-sm text-muted-foreground">Loading venues…</p>;
  }
  if (error && venues.length === 0) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {error}
      </p>
    );
  }
  // Saved bookmarks stay reachable even with no comparables selected.
  if (comparableCount === 0 && listCounts.saved === 0) {
    return (
      <div className="aa-glass-card rounded-2xl p-6 text-center">
        <p className="font-medium text-foreground">Add comparable artists to see venues.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          We'll show you where artists like you have actually played.
        </p>
        <Button className="mt-4" onClick={onGoToArtists}>
          Choose comparable artists
        </Button>
      </div>
    );
  }
  if (venues.length === 0) {
    return (
      <div className="aa-glass-card rounded-2xl p-6 text-center">
        <p className="font-medium text-foreground">No venues yet.</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Touring history will appear here as it becomes available. You can keep exploring while updates are pending.
        </p>
        <Button className="mt-4" variant="secondary" onClick={onGoToArtists}>
          Go to comparable artists
        </Button>
      </div>
    );
  }

  const locationOf = (venue: GraphVenue) =>
    formatLocation([venue.city, venue.region]) || formatLocation([venue.country]) || "Location unknown";
  const canPitch = (venue: GraphVenue) => Boolean(venue.global_venue_id) && venue.pitchable !== false;

  const venueKey = (venue: GraphVenue, index: number) => venue.global_venue_id ?? `venue-${venue.venue_ids.join("-") || index}`;
  const openVenue = openVenueKey ? venues.find((venue, index) => venueKey(venue, index) === openVenueKey) ?? null : null;

  /** "In Booking · Prospecting" / "In Booking · Contacted 3 days ago"; null when not in Booking. */
  const bookingLabel = (venue: GraphVenue): string | null => {
    const state = bookingState(venue);
    if (state === "contacted") {
      return `In Booking · ${venue.private_state.contacted_at ? formatContactedRecency(venue.private_state.contacted_at) : "Contacted"}`;
    }
    return state === "prospect" ? "In Booking · Prospecting" : null;
  };

  const statusText = (venue: GraphVenue): string => {
    const parts: string[] = [];
    if (venue.identity?.status === "needs_review") parts.push("Needs review");
    if (venue.private_state.saved) parts.push("Saved");
    const state = bookingState(venue);
    parts.push(state === "none" ? "Not in Booking" : state === "prospect" ? "In Booking · Prospecting" : "In Booking · Contacted");
    if (venue.private_state.dismissed) parts.push("Hidden");
    return parts.join(" · ");
  };

  /** Save toggle: a Discover bookmark only. Filled star = saved; click again to un-save. */
  const renderStar = (venue: GraphVenue) => {
    if (!canPitch(venue)) return null;
    const saved = venue.private_state.saved;
    const saving = savingId === venue.global_venue_id;
    return (
      <button
        type="button"
        aria-label={saved ? "Saved venue" : "Save venue"}
        aria-pressed={saved}
        title={saved ? "Saved · click to remove from Saved" : "Save venue (bookmark only)"}
        disabled={saving}
        onClick={(event) => {
          event.stopPropagation();
          void handleSaveToggle(venue);
        }}
        className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 ${
          saved ? "text-amber-500" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        <Star className="h-4 w-4" fill={saved ? "currentColor" : "none"} aria-hidden="true" />
      </button>
    );
  };

  /** Add to Booking (until it's in Booking) and Pitch; both reuse the one Booking record. */
  const renderBookingActions = (venue: GraphVenue) => {
    if (!canPitch(venue)) return null;
    const inBooking = bookingState(venue) !== "none";
    const adding = bookingId === venue.global_venue_id;
    return (
      <>
        {!inBooking && (
          <Button
            size="sm"
            variant="secondary"
            className="h-7 px-3 text-xs"
            disabled={adding}
            onClick={() => void handleAddToBooking(venue)}
            aria-label={`Add ${venue.name} to Booking`}
          >
            {adding ? "Adding…" : "Add to Booking"}
          </Button>
        )}
        <Button
          size="sm"
          className="h-7 px-3 text-xs"
          onClick={() => {
            setOpenVenueKey(null); // one sheet at a time
            setPitchVenue(venue);
          }}
          aria-label={`Pitch ${venue.name}`}
        >
          Pitch
        </Button>
      </>
    );
  };

  const renderName = (venue: GraphVenue, key: string, className: string) => (
    <button
      type="button"
      aria-haspopup="dialog"
      aria-label={`View ${venue.name}`}
      title={venue.name}
      className={`text-left hover:underline focus-visible:underline focus-visible:outline-none ${className}`}
      onClick={() => setOpenVenueKey(key)}
    >
      {venue.name}
    </button>
  );

  const renderEvidence = (venue: GraphVenue, key: string, inTable = false) =>
    venue.in_comparable_graph === false ? (
      <span className="text-xs text-muted-foreground">Saved earlier</span>
    ) : (
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label={`Comparable artists (${venue.comparable_artist_count})`}
        className="text-xs font-medium text-primary hover:underline focus-visible:underline focus-visible:outline-none"
        onClick={() => setOpenVenueKey(key)}
      >
        {inTable ? venue.comparable_artist_count : `Comparable artists (${venue.comparable_artist_count})`}
      </button>
    );

  const renderHideRestore = (venue: GraphVenue) => {
    const busy = dismissingId === venue.global_venue_id;
    if (venue.private_state.dismissed) {
      return (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={() => void handleDismissal(venue, false)}
          disabled={busy}
          aria-label={`Restore ${venue.name}`}
        >
          {busy ? "Restoring…" : "Restore"}
        </Button>
      );
    }
    // Venues in Booking carry booking history and are managed from Booking, not hidden here.
    if (!venue.global_venue_id || bookingState(venue) !== "none") return null;
    return (
      <button
        type="button"
        className="inline-flex items-center gap-1 rounded px-1.5 py-1 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        onClick={() => void handleDismissal(venue, true)}
        disabled={busy}
        aria-label={`Dismiss ${venue.name}`}
        title="Hide from my Discover results (you can restore it later)"
      >
        <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
        Hide
      </button>
    );
  };

  return (
    <section className="space-y-3" aria-label="Venues">
      <div className="inline-flex rounded-md border border-border p-0.5" role="group" aria-label="Venue list">
        {(
          [
            ["recommended", "Recommended"],
            ["saved", "Saved"],
            ["hidden", "Hidden"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-pressed={list === id}
            onClick={() => setList(id)}
            className={`rounded px-3 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              list === id ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {label} ({listCounts[id]})
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3" role="group" aria-label="Filter venues">
        <div className="min-w-[180px] flex-1">
          <Input
            placeholder="Search venue or city"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search venues"
          />
        </div>
        <label className="flex flex-col text-xs text-muted-foreground">
          State / region
          <select className={selectClass} value={region} onChange={(event) => setRegion(event.target.value)} aria-label="State or region">
            <option value="all">All</option>
            {regionOptions.map(([value, count]) => (
              <option key={value} value={value}>
                {value} ({count})
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-muted-foreground">
          Comparable artist
          <select
            className={selectClass}
            value={comparableId}
            onChange={(event) => handleComparableChange(event.target.value)}
            aria-label="Comparable artist"
          >
            <option value="all">All</option>
            {comparableOptions.map(([id, name]) => (
              <option key={id} value={String(id)}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-muted-foreground">
          Booking
          <select
            className={selectClass}
            value={status}
            onChange={(event) => setStatus(event.target.value as "all" | BookingState)}
            aria-label="Booking status"
          >
            <option value="all">All</option>
            {(Object.keys(BOOKING_FILTER_LABELS) as BookingState[]).map((key) => (
              <option key={key} value={key}>
                {BOOKING_FILTER_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs text-muted-foreground">
          Played by
          <select
            className={selectClass}
            value={minArtists}
            onChange={(event) => setMinArtists(Number(event.target.value))}
            aria-label="Minimum comparable artists"
          >
            {Array.from({ length: maxObserved }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>
                {n === 1 ? "Any" : `${n}+ artists`}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <p role="status" aria-live="polite">
          Showing {filtered.length} of {listCounts[list]} venues
          {list === "recommended" && listCounts.hidden > 0 ? ` · ${listCounts.hidden} hidden` : ""}
        </p>
        {filtersActive && (
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={clearFilters}>
            Clear filters
          </Button>
        )}
        <div className="ml-auto inline-flex rounded-md border border-border p-0.5" role="group" aria-label="Display mode">
          {(
            [
              ["cards", "Cards", LayoutGrid],
              ["table", "Table", Rows3],
            ] as const
          ).map(([mode, label, Icon]) => (
            <button
              key={mode}
              type="button"
              aria-pressed={view === mode}
              onClick={() => changeView(mode)}
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                view === mode ? "bg-primary/15 text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {listCounts[list] > 0
            ? "No venues match these filters."
            : list === "saved"
              ? "No saved venues yet. Use the star on a venue to save it for later."
              : list === "hidden"
                ? "No hidden venues."
                : comparableCount === 0
                  ? "Add comparable artists to see recommended venues."
                  : "No recommended venues right now."}
        </p>
      )}

      {view === "cards" ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(15rem,1fr))] gap-2">
          {filtered.map((venue, index) => {
            const key = venueKey(venue, index);
            const dismissed = Boolean(venue.private_state.dismissed);
            const needsReview = venue.identity?.status === "needs_review";
            const saveError = venue.global_venue_id ? saveErrors[venue.global_venue_id] : undefined;
            return (
              <article
                key={key}
                className={`aa-glass-card flex flex-col gap-2 rounded-xl p-3 ${dismissed ? "opacity-75" : ""}`}
                aria-label={venue.name}
              >
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="line-clamp-2 text-base font-semibold leading-snug text-foreground">
                      {renderName(venue, key, "")}
                    </h3>
                    <p className="truncate text-xs text-muted-foreground">{locationOf(venue)}</p>
                  </div>
                  {renderStar(venue)}
                </div>

                {dismissed && (
                  <p className="text-[11px] font-medium text-muted-foreground">
                    <EyeOff className="mr-1 inline h-3 w-3 align-[-2px]" aria-hidden="true" />
                    Hidden from your recommended venues
                  </p>
                )}

                {needsReview && (
                  <p className="text-[11px] leading-snug text-amber-600 dark:text-amber-300">
                    <span className="font-semibold">Needs review</span> ·{" "}
                    {venue.identity.reason ?? "This venue's identity needs review."} It can't be saved or added to Booking yet.
                  </p>
                )}

                {(canPitch(venue) || bookingLabel(venue)) && (
                  <div className="flex flex-wrap items-center gap-2">
                    {renderBookingActions(venue)}
                    {bookingLabel(venue) && <span className="text-[11px] font-medium text-muted-foreground">{bookingLabel(venue)}</span>}
                  </div>
                )}
                {!venue.global_venue_id && (
                  <p className="text-[11px] text-muted-foreground" title="This venue can't be saved yet.">
                    Can't save yet
                  </p>
                )}

                <div className="mt-auto flex items-center justify-between gap-2">
                  {renderEvidence(venue, key)}
                  {renderHideRestore(venue)}
                </div>
                {saveError && (
                  <p role="alert" className="text-xs text-destructive">
                    {saveError}
                  </p>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border/60">
          <Table aria-label="Venues">
            <TableHeader>
              <TableRow>
                <TableHead>Venue</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Comparable artists</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last contacted</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((venue, index) => {
                const key = venueKey(venue, index);
                const saveError = venue.global_venue_id ? saveErrors[venue.global_venue_id] : undefined;
                return (
                  <TableRow key={key} aria-label={venue.name} className={venue.private_state.dismissed ? "opacity-75" : ""}>
                    <TableCell className="max-w-[16rem]">
                      {renderName(venue, key, "block max-w-full truncate font-medium text-foreground")}
                      {saveError && (
                        <span role="alert" className="block text-xs text-destructive">
                          {saveError}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{locationOf(venue)}</TableCell>
                    <TableCell>{renderEvidence(venue, key, true)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs">{statusText(venue)}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {venue.private_state.contacted_at ? (
                        <span title={venue.private_state.contacted_at}>
                          {formatContactedRecency(venue.private_state.contacted_at).replace(/^Contacted /, "")}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1.5">
                        {renderStar(venue)}
                        {renderBookingActions(venue)}
                        {renderHideRestore(venue)}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <VenueDetailSheet
        artistId={artistId}
        venue={openVenue}
        onClose={() => setOpenVenueKey(null)}
        actions={
          openVenue ? (
            <>
              {renderStar(openVenue)}
              {renderBookingActions(openVenue)}
              {renderHideRestore(openVenue)}
            </>
          ) : null
        }
      />
      <PitchSheet
        artistId={artistId}
        globalVenueId={pitchVenue?.global_venue_id ?? null}
        venueName={pitchVenue?.name}
        onClose={() => setPitchVenue(null)}
        onStateChange={onVenuePitchState}
      />
    </section>
  );
}
